import React from "react";
import { motion } from "framer-motion";
import {
  Award, ShieldCheck, ClipboardList, MessageCircle, ClockArrowUp,
  HeartHandshake, BadgeCheck, Sparkles,
} from "lucide-react";

const PILLARS = [
  { icon: HeartHandshake, t: "Atención centrada en la paciente", d: "Tu experiencia y bienestar guían cada decisión clínica." },
  { icon: ClockArrowUp, t: "Procesos ágiles y eficientes", d: "Tiempos optimizados sin sacrificar calidad ni calidez." },
  { icon: MessageCircle, t: "Comunicación clara", d: "Información comprensible y disponible en cada etapa." },
  { icon: ClipboardList, t: "Seguimiento continuo", d: "Acompañamiento estructurado entre consultas y estudios." },
  { icon: Award, t: "Calidad clínica", d: "Estándares actualizados respaldados por evidencia médica." },
  { icon: ShieldCheck, t: "Seguridad del paciente", d: "Protocolos pensados para prevenir riesgos y complicaciones." },
];

export default function WhyChooseSection() {
  return (
    <section
      data-testid="why-choose-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-snow to-white" />
      <div className="absolute -top-20 left-10 w-80 h-80 rounded-full bg-brand-bright/10 blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6"
          >
            <p className="text-xs uppercase tracking-[0.28em] text-brand-medical mb-3">
              <Sparkles className="inline w-3.5 h-3.5 mr-1.5" strokeWidth={1.6} />
              ¿Por qué elegir al Dr. Jairo Franco?
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-deep leading-[1.05]">
              Visión médica <em className="not-italic text-gradient-blue">y administrativa</em> al servicio de tu salud.
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-6 space-y-4 text-brand-deep/75 font-light leading-relaxed text-lg"
          >
            <p>
              Además de mi formación como <strong className="text-brand-deep font-medium">Médico Especialista en Ginecología y Obstetricia</strong>,
              cuento con una <strong className="text-brand-deep font-medium">Maestría en Administración Hospitalaria</strong>,
              lo que me permite ofrecer una atención médica organizada, segura y
              centrada en la experiencia de cada paciente.
            </p>
            <p>
              Mi objetivo no es únicamente resolver un problema médico, sino
              acompañar a cada mujer mediante procesos claros, seguimiento oportuno
              y una atención integral basada en estándares de calidad y seguridad
              del paciente.
            </p>
          </motion.div>
        </div>

        {/* Pillar badges */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.t}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.07, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4 }}
              data-testid={`pillar-${i}`}
              className="group relative rounded-3xl bg-white hairline premium-shadow hover:premium-shadow-hover p-6 transition-shadow duration-500 overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-brand-bright/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-brand-cloud to-brand-silver ring-1 ring-brand-silver/60 flex items-center justify-center flex-shrink-0">
                  <p.icon className="w-5 h-5 text-brand-medical" strokeWidth={1.4} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <BadgeCheck className="w-3.5 h-3.5 text-brand-medical" strokeWidth={1.8} />
                    <p className="text-[10px] uppercase tracking-[0.22em] text-brand-medical">Indicador clínico</p>
                  </div>
                  <h3 className="font-serif text-lg text-brand-deep leading-tight">{p.t}</h3>
                  <p className="text-sm text-brand-deep/65 font-light leading-relaxed mt-1.5">{p.d}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
